import { isPast } from "date-fns";
import { ExportReportType, REPORT_EXPORTS } from "@sscs/types";
import { SHAREABLE_REPORTS_ROUTE } from "Constants";
import { SharedReportSettings } from "Types";
import { generateEmailSchema, getBaseRoute } from "Utils";
import * as yup from "Utils/yup";
import {
  LinkNameUseCase,
  SharedReportForm,
  SharedReportInfo,
  SharedReportLinkNameForm,
  SharedReportLinkRecipient,
  SharedReportPreviousRecipientsForm,
  SharedReportRecipientsForm,
  SharedReportRestrictionsForm,
} from "./types";

export const REPORT_EXPORT_ENDPOINT: Record<ExportReportType, string> = {
  cve: "rl-cve",
  cycloneDX: "cyclone-dx",
  json: "rl-json",
  safe: "rl-safe",
  sarif: "sarif",
  spdx: "spdx",
  summaryPdf: "rl-summary-pdf",
  uri: "rl-uri",
};

export const SHARED_REPORT_LINK_EXPIRY_DAYS = [1, 7, 14, 30, 60, 90];

export const EMAIL_MESSAGE_PLACEHOLDER = `Hello,\n\nThis report was shared with you to check the quality of the software and identify issues that need to be resolved.\n\nIf you're having trouble accessing the report, please contact me.\n\nKind regards!`;

export const LINK_NAME_LABEL: Record<LinkNameUseCase, string> = {
  vendor: "Vendor",
  customer: "Customer",
  internalStakeholder: "Internal stakeholder",
  externalAuditor: "External auditor",
  customerSupport: "ReversingLabs Support",
} as const;

export const DEFAULT_RESTRICTIONS: SharedReportRestrictionsForm = {
  expires_in: 30,
  package_download_enabled: false,
  cyclonedx_export_enabled: true,
  rl_cve_export_enabled: true,
  rl_json_export_enabled: true,
  rl_safe_export_enabled: true,
  rl_uri_export_enabled: true,
  sarif_export_enabled: true,
  spdx_export_enabled: true,
  rl_summary_pdf_export_enabled: true,
  password_protected: false,
};

export const DEFAULT_RECIPIENTS_FORM_DATA: SharedReportRecipientsForm = {
  recipients: [],
  message: EMAIL_MESSAGE_PLACEHOLDER,
  send_copy_to_me: false,
};

export const DEFAULT_SETTINGS = {
  label: "",
  ...DEFAULT_RESTRICTIONS,
  ...DEFAULT_RECIPIENTS_FORM_DATA,
};

export const getCustomerSupportEmail = (): string => {
  return window.env?.supportEmail?.value ?? "";
};

export const SUPPORT_CASE_DEFAULT_SETTINGS: SharedReportLinkNameForm = {
  label: LINK_NAME_LABEL.customerSupport,
  use_case: "customerSupport",
};

export const SUPPORT_CASE_RESTRICTION_OVERRIDES: Partial<SharedReportRestrictionsForm> =
  {
    package_download_enabled: true,
    password_protected: true,
  };

export type SharedReportExportOptions = Record<ExportReportType, boolean>;

type SharedReportExport = (typeof REPORT_EXPORTS)[keyof typeof REPORT_EXPORTS];

export const getLinkNameStepInitialValues = (
  existingFormData: SharedReportLinkNameForm,
  isSupportDefault?: boolean,
): SharedReportLinkNameForm =>
  isSupportDefault
    ? SUPPORT_CASE_DEFAULT_SETTINGS
    : {
        label: existingFormData.label,
        use_case: existingFormData.use_case,
      };

export const getRecipientsFormInitialValues = (
  label: string,
  existingFormData?: SharedReportRecipientsForm,
): SharedReportRecipientsForm => {
  if (label !== LINK_NAME_LABEL.customerSupport) {
    return existingFormData ?? DEFAULT_RECIPIENTS_FORM_DATA;
  }

  if (existingFormData) {
    if (existingFormData.recipients.includes(getCustomerSupportEmail())) {
      return existingFormData;
    }

    return {
      ...existingFormData,
      recipients: existingFormData.recipients.concat(getCustomerSupportEmail()),
    };
  } else {
    return {
      ...DEFAULT_RECIPIENTS_FORM_DATA,
      recipients: [getCustomerSupportEmail()],
    };
  }
};

export const getRestrictionsStepInitialValues = (
  label: string,
  existingFormData?: SharedReportRestrictionsForm,
): SharedReportRestrictionsForm => {
  if (label !== LINK_NAME_LABEL.customerSupport) {
    return existingFormData ?? DEFAULT_RESTRICTIONS;
  }

  if (existingFormData) {
    return {
      ...existingFormData,
      ...SUPPORT_CASE_RESTRICTION_OVERRIDES,
    };
  } else {
    return {
      ...DEFAULT_RESTRICTIONS,
      ...SUPPORT_CASE_RESTRICTION_OVERRIDES,
    };
  }
};

export const getSharedReportFormDataFromInitialSettings = (
  settings: SharedReportSettings,
): SharedReportForm => ({
  ...settings,
  ...DEFAULT_RECIPIENTS_FORM_DATA,
});

export const getSharedReportExportSettings = (
  options: SharedReportExportOptions,
): {
  enabledExports: SharedReportExport[];
  disabledExports: SharedReportExport[];
} => {
  const enabledExports: SharedReportExport[] = [];
  const disabledExports: SharedReportExport[] = [];

  Object.entries(options).forEach(
    ([option, isEnabled]: [ExportReportType, boolean]) => {
      if (isEnabled) {
        enabledExports.push(REPORT_EXPORTS[option]);
      } else {
        disabledExports.push(REPORT_EXPORTS[option]);
      }
    },
  );

  return { enabledExports, disabledExports };
};

export const updateRecipientsFormWithPreviousRecipients = (
  formData: SharedReportRecipientsForm,
  previousRecipients: {
    email: SharedReportLinkRecipient["email"];
    lastOpened: SharedReportLinkRecipient["last_opened"];
    isSelected: boolean;
  }[],
): SharedReportRecipientsForm => {
  const selectedRecipients: string[] = previousRecipients
    .filter((recipient) => recipient.isSelected)
    .map((recipient) => recipient.email);

  const unselectedRecipients: string[] = previousRecipients
    .filter((recipient) => !recipient.isSelected)
    .map((recipient) => recipient.email);

  const newRecipients: string[] = formData.recipients
    ? formData.recipients
        .filter(
          (recipientEmail) => !unselectedRecipients.includes(recipientEmail),
        )
        .concat(selectedRecipients)
    : selectedRecipients;

  const uniqueRecipients = new Set(newRecipients);

  return {
    recipients: Array.from(uniqueRecipients),
    message: formData.message ?? DEFAULT_RECIPIENTS_FORM_DATA.message,
    send_copy_to_me:
      formData.send_copy_to_me || DEFAULT_RECIPIENTS_FORM_DATA.send_copy_to_me,
  };
};

export const isSharedReportLinkExpired = (sharedReport: SharedReportInfo) =>
  sharedReport.invalid || // all invalid links are expired (stopping the link sharing makes the link invalid and expired)
  isPast(new Date(sharedReport.settings.expiration_date));

export const getSharedReportLink = (token: string): string =>
  `${getBaseRoute()}/${SHAREABLE_REPORTS_ROUTE}/${token}`;

export const restrictionsFormSchema: yup.SchemaOf<SharedReportRestrictionsForm> =
  yup.object({
    expires_in: yup.number().oneOf(SHARED_REPORT_LINK_EXPIRY_DAYS).required(),
    package_download_enabled: yup.boolean().required(),
    cyclonedx_export_enabled: yup.boolean().required(),
    rl_cve_export_enabled: yup.boolean().required(),
    rl_json_export_enabled: yup.boolean().required(),
    rl_safe_export_enabled: yup.boolean().required(),
    rl_uri_export_enabled: yup.boolean().required(),
    sarif_export_enabled: yup.boolean().required(),
    spdx_export_enabled: yup.boolean().required(),
    rl_summary_pdf_export_enabled: yup.boolean().required(),
    password_protected: yup.boolean().required(),
  });

export const getSharedReportPreviousRecipientsFormSchema = (
  recipientsRequired: boolean = false,
): yup.SchemaOf<SharedReportPreviousRecipientsForm> => {
  const recipientsSchema = yup
    .array()
    .of(generateEmailSchema())
    .max(100, "${max} maximum email addresses allowed")
    .required();
  return yup.object({
    recipients: recipientsRequired
      ? recipientsSchema.min(1, "At least ${min} email address is required")
      : recipientsSchema,
  });
};

export const getSharedReportRecipientsFormSchema = (
  recipientsRequired: boolean = false,
): yup.SchemaOf<SharedReportRecipientsForm> =>
  getSharedReportPreviousRecipientsFormSchema(recipientsRequired).concat(
    yup.object({
      message: yup
        .string()
        .max(500, "Custom message cannot be longer than ${max} characters")
        .optional(),
      send_copy_to_me: yup.boolean().required(),
    }),
  );

export const getSharedReportLinkNameSchema =
  (): yup.SchemaOf<SharedReportLinkNameForm> =>
    yup.object({
      label: yup
        .string()
        .transform((value) => value.trim())
        .required("Link name is required")
        .max(200, "Max link name length is 200 characters"),
      use_case: yup.mixed<LinkNameUseCase>().optional(),
    });
